/**
 * MRP Label Generator Component
 * Version: 1.0.0
 * Last Updated: 2025-12-01
 *
 * Description:
 *   Merge multiple product label PDFs based on Excel quantity data.
 *   - Tab 1: Generate Labels (Upload Excel -> Preview -> Generate)
 *   - Tab 2: Manage PDF Library (Upload/List/Delete PDFs in Cloud Storage)
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    TextField,
} from '@mui/material';
import {
    Upload as UploadIcon,
    Download as DownloadIcon,
    CheckCircle as CheckCircleIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    PictureAsPdf as PdfIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    Search as SearchIcon,
    Block as BlockIcon,
    Label as LabelIcon,
    Restore as RestoreIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, useGridApiRef } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { b2cOpsAPI } from '../../api';

interface FileUploadAreaProps {
    onFileSelect: (file: File) => void;
    accept: string;
    label: string;
    loading: boolean;
}

// --- Sub-component: File Upload Area ---
const FileUploadArea: React.FC<FileUploadAreaProps> = ({ onFileSelect, accept, label, loading }) => {
    const [isDragging, setIsDragging] = useState(false);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) onFileSelect(droppedFile);
    };

    return (
        <Paper
            sx={{
                p: 3,
                border: isDragging ? '2px dashed #1976d2' : '2px dashed transparent',
                bgcolor: isDragging ? 'action.hover' : 'background.paper',
                transition: 'all 0.2s ease',
                textAlign: 'center'
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <Typography variant="h6" gutterBottom>
                {label}
            </Typography>
            <Box sx={{ mt: 2, py: 2 }}>
                <input
                    accept={accept}
                    style={{ display: 'none' }}
                    id="file-upload-mrp"
                    type="file"
                    onChange={(e) => e.target.files && e.target.files[0] && onFileSelect(e.target.files[0])}
                />
                <label htmlFor="file-upload-mrp">
                    <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                        disabled={loading}
                        size="large"
                    >
                        {loading ? 'Loading...' : 'Choose File or Drag & Drop Here'}
                    </Button>
                </label>
            </Box>
        </Paper>
    );
};

interface MRPPreviewData {
    total_items: number;
    total_pages: number;
    valid_rows: number;
    missing_pdfs: string[];
    data: any[];
}

interface LibraryFile {
    name: string;
    product_name: string;
    size: number;
    created_at: string;
}

export default function MrpLabelGenerator() {
    const { enqueueSnackbar } = useSnackbar();
    const previewGridRef = useGridApiRef();
    const libraryGridRef = useGridApiRef();
    const [tabValue, setTabValue] = useState(0);

    // --- Tab 1 State: Generation ---
    const [excelFile, setExcelFile] = useState<File | null>(null);
    const [previewData, setPreviewData] = useState<MRPPreviewData | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);

    // --- Tab 2 State: Library ---
    const [libraryFiles, setLibraryFiles] = useState<LibraryFile[]>([]);
    const [loadingLibrary, setLoadingLibrary] = useState(false);
    const [uploadingPdf, setUploadingPdf] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [isLibraryFullscreen, setIsLibraryFullscreen] = useState(false);
    const [librarySearchText, setLibrarySearchText] = useState('');

    // --- Tab 3 State: Exclusions ---
    const [exclusions, setExclusions] = useState<any[]>([]);
    const [loadingExclusions, setLoadingExclusions] = useState(false);
    const [exclusionSearchText, setExclusionSearchText] = useState('');
    const [addExclusionOpen, setAddExclusionOpen] = useState(false);
    const [newProductId, setNewProductId] = useState('');
    const [newVariationId, setNewVariationId] = useState('');

    // Fetch library on tab change
    useEffect(() => {
        if (tabValue === 1) {
            fetchLibrary();
        }
    }, [tabValue]);

    // --- Tab 1 Functions ---
    const handleExcelUpload = async (file: File) => {
        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            enqueueSnackbar('Invalid file type. Please upload Excel file.', { variant: 'error' });
            return;
        }

        setExcelFile(file);
        setLoadingPreview(true);
        setPreviewData(null);

        try {
            const data = await b2cOpsAPI.previewMrpLabels(file);
            setPreviewData(data);
            enqueueSnackbar('Excel file processed successfully!', { variant: 'success' });
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to process file', { variant: 'error' });
            setExcelFile(null);
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleGenerate = async () => {
        if (!previewData) return;
        setGenerating(true);
        try {
            const response = await b2cOpsAPI.generateMrpLabels(previewData.data);
            const blob = response.data;

            // Download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Extract filename from Content-Disposition header (backend provides standardized name)
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'mrp_labels.pdf'; // Fallback

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            enqueueSnackbar('Labels generated successfully!', { variant: 'success' });
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to generate labels', { variant: 'error' });
        } finally {
            setGenerating(false);
        }
    };

    // --- Tab 2 Functions ---
    const fetchLibrary = async () => {
        setLoadingLibrary(true);
        try {
            const files = await b2cOpsAPI.listMrpLibrary();
            setLibraryFiles(files);
        } catch (error) {
            enqueueSnackbar('Failed to load PDF library', { variant: 'error' });
        } finally {
            setLoadingLibrary(false);
        }
    };

    const handlePdfUpload = async (file: File) => {
        if (!file.name.toLowerCase().endsWith('.pdf')) {
            enqueueSnackbar('Only PDF files allowed', { variant: 'error' });
            return;
        }

        setUploadingPdf(true);
        try {
            await b2cOpsAPI.uploadMrpPdf(file);
            enqueueSnackbar(`Uploaded ${file.name}`, { variant: 'success' });
            fetchLibrary(); // Refresh list
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Upload failed', { variant: 'error' });
        } finally {
            setUploadingPdf(false);
        }
    };

    const handleDeletePdf = async () => {
        if (!deleteConfirm) return;
        try {
            await b2cOpsAPI.deleteMrpPdf(deleteConfirm);
            enqueueSnackbar('PDF deleted', { variant: 'success' });
            fetchLibrary();
        } catch (error) {
            enqueueSnackbar('Delete failed', { variant: 'error' });
        } finally {
            setDeleteConfirm(null);
        }
    };

    const handleDownloadPdf = async (filename: string) => {
        try {
            const blob = await b2cOpsAPI.downloadMrpPdf(filename);

            // Download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            enqueueSnackbar(`Downloaded ${filename}`, { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Download failed', { variant: 'error' });
        }
    };

    // --- Exclusion Handlers ---
    const fetchExclusions = async () => {
        setLoadingExclusions(true);
        try {
            const data = await b2cOpsAPI.listMrpExclusions();
            setExclusions(data);
        } catch (error) {
            enqueueSnackbar('Failed to load exclusions', { variant: 'error' });
        } finally {
            setLoadingExclusions(false);
        }
    };

    const handleToggleExclusion = async (row: any) => {
        try {
            if (row.is_excluded) {
                // Find the exclusion ID and remove it
                const exclusion = exclusions.find(
                    e => e.product_id === row.item_id &&
                        (e.variation_id === row.variation_id || (!e.variation_id && row.variation_id === 0))
                );
                if (exclusion) {
                    await b2cOpsAPI.removeMrpExclusion(exclusion.id);
                    enqueueSnackbar('Product included in label generation', { variant: 'success' });
                }
            } else {
                // Add exclusion
                await b2cOpsAPI.addMrpExclusion(row.item_id, row.variation_id === 0 ? null : row.variation_id);
                enqueueSnackbar('Product excluded from label generation', { variant: 'success' });
            }
            // Refresh preview and exclusions
            if (excelFile) {
                await handleExcelUpload(excelFile);
            }
            fetchExclusions();
        } catch (error) {
            enqueueSnackbar('Failed to update exclusion', { variant: 'error' });
        }
    };

    const handleRemoveExclusion = async (exclusionId: number) => {
        try {
            await b2cOpsAPI.removeMrpExclusion(exclusionId);
            enqueueSnackbar('Product removed from exclusion list', { variant: 'success' });
            fetchExclusions();
            // Refresh preview if file is loaded
            if (excelFile) {
                await handleExcelUpload(excelFile);
            }
        } catch (error) {
            enqueueSnackbar('Failed to remove exclusion', { variant: 'error' });
        }
    };

    const handleAddExclusionManually = async () => {
        if (!newProductId) {
            enqueueSnackbar('Product ID is required', { variant: 'error' });
            return;
        }

        try {
            const productId = parseInt(newProductId);
            const variationId = newVariationId ? parseInt(newVariationId) : null;

            await b2cOpsAPI.addMrpExclusion(productId, variationId);
            enqueueSnackbar('Product excluded from label generation', { variant: 'success' });

            // Reset form and close dialog
            setNewProductId('');
            setNewVariationId('');
            setAddExclusionOpen(false);

            // Refresh exclusions list
            fetchExclusions();

            // Refresh preview if file is loaded
            if (excelFile) {
                await handleExcelUpload(excelFile);
            }
        } catch (error) {
            enqueueSnackbar('Failed to add exclusion', { variant: 'error' });
        }
    };

    // Fetch exclusions when tab changes to exclusions tab
    useEffect(() => {
        if (tabValue === 2) {
            fetchExclusions();
        }
    }, [tabValue]);


    // --- Columns ---
    const previewColumns: GridColDef[] = [
        { field: 'id', headerName: '#', width: 70 },
        { field: 'item_id', headerName: 'Item ID', width: 100 },
        { field: 'variation_id', headerName: 'Variation ID', width: 120 },
        {
            field: 'product_name',
            headerName: 'Product Name',
            width: 400,
            minWidth: 250,
        },
        { field: 'quantity', headerName: 'Qty', width: 80 },
        {
            field: 'pdf_filename',
            headerName: 'Status',
            width: 200,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {params.row.is_excluded ? (
                        <Chip
                            label="No Label"
                            color="warning"
                            size="small"
                            icon={<BlockIcon />}
                        />
                    ) : params.row.is_available ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                        <Tooltip title="PDF Missing in Library">
                            <Chip label="Missing" color="error" size="small" />
                        </Tooltip>
                    )}
                    {params.value}
                </Box>
            )
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            renderCell: (params) => (
                <Tooltip title={params.row.is_excluded ? "Include in labels" : "Exclude from labels"}>
                    <IconButton
                        color={params.row.is_excluded ? "primary" : "default"}
                        onClick={() => handleToggleExclusion(params.row)}
                        size="small"
                    >
                        {params.row.is_excluded ? <LabelIcon /> : <BlockIcon />}
                    </IconButton>
                </Tooltip>
            )
        },
    ];

    const libraryColumns: GridColDef[] = [
        {
            field: 'name',
            headerName: 'Filename',
            width: 150,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PdfIcon color="error" />
                    {params.value}
                </Box>
            )
        },
        {
            field: 'product_name',
            headerName: 'Product Name',
            width: 400,
            minWidth: 250,
        },
        {
            field: 'size',
            headerName: 'Size (KB)',
            width: 100,
            valueGetter: (params) => Math.round(params.row.size / 1024)
        },
        {
            field: 'created_at',
            headerName: 'Uploaded At',
            width: 180,
            valueFormatter: (params) => new Date(params.value).toLocaleString()
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Download PDF">
                        <IconButton
                            color="primary"
                            onClick={() => handleDownloadPdf(params.row.name)}
                        >
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete PDF">
                        <IconButton
                            color="error"
                            onClick={() => setDeleteConfirm(params.row.name)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    ];

    const exclusionColumns: GridColDef[] = [
        { field: 'product_id', headerName: 'Product ID', width: 120 },
        { field: 'variation_id', headerName: 'Variation ID', width: 120 },
        { field: 'product_name', headerName: 'Product Name', width: 400, minWidth: 250 },
        { field: 'sku', headerName: 'SKU', width: 150 },
        {
            field: 'created_at',
            headerName: 'Added On',
            width: 180,
            valueFormatter: (params) => new Date(params.value).toLocaleString()
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            renderCell: (params) => (
                <Tooltip title="Remove from exclusion list">
                    <IconButton
                        color="primary"
                        onClick={() => handleRemoveExclusion(params.row.id)}
                        size="small"
                    >
                        <RestoreIcon />
                    </IconButton>
                </Tooltip>
            )
        }
    ];

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                    🔖 MRP Label Generator
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Merge product label PDFs based on Excel quantity data
                </Typography>
            </Box>

            <Paper sx={{ mb: 3 }}>
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label="📊 Generate Labels" />
                    <Tab label="📂 Manage PDF Library" />
                    <Tab label="🚫 Excluded Products" />
                </Tabs>

                {/* TAB 1: GENERATE */}
                {tabValue === 0 && (
                    <Box sx={{ p: 3 }}>
                        <FileUploadArea
                            label="Upload Excel File (.xlsx)"
                            accept=".xlsx,.xls"
                            onFileSelect={handleExcelUpload}
                            loading={loadingPreview}
                        />

                        {excelFile && (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                                <CheckCircleIcon color="success" />
                                <Typography>{excelFile.name}</Typography>
                            </Box>
                        )}

                        {previewData && (
                            <Box sx={{ mt: 4 }}>
                                <Typography variant="h6" gutterBottom>Summary</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">Total Items</Typography>
                                        <Typography variant="h5">{previewData.total_items}</Typography>
                                    </Paper>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">Total Pages</Typography>
                                        <Typography variant="h5">{previewData.total_pages}</Typography>
                                    </Paper>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">Missing PDFs</Typography>
                                        <Typography variant="h5" color={previewData.missing_pdfs.length > 0 ? "error" : "success"}>
                                            {previewData.missing_pdfs.length}
                                        </Typography>
                                    </Paper>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">Output Files</Typography>
                                        <Typography variant="h5">
                                            {Math.ceil(previewData.total_pages / 25)}
                                            {previewData.total_pages > 25 && <Chip label="ZIP" size="small" color="info" sx={{ ml: 1 }} />}
                                        </Typography>
                                    </Paper>
                                </Box>

                                {previewData.missing_pdfs.length > 0 && (
                                    <Alert severity="warning" sx={{ mb: 3 }}>
                                        ⚠️ {previewData.missing_pdfs.length} PDFs are missing from the library. These items will be skipped.
                                        <Box sx={{ mt: 1 }}>
                                            Missing: {previewData.missing_pdfs.slice(0, 10).join(', ')}
                                            {previewData.missing_pdfs.length > 10 && '...'}
                                        </Box>
                                    </Alert>
                                )}

                                <Typography variant="h6" gutterBottom>Data Preview</Typography>
                                <Box
                                    className={isPreviewFullscreen ? 'datagrid-fullscreen' : ''}
                                    sx={{
                                        height: isPreviewFullscreen ? '100vh' : 400,
                                        width: '100%',
                                        mb: 3,
                                        transition: 'height 0.3s ease',
                                    }}
                                >
                                    <DataGrid
                                        apiRef={previewGridRef}
                                        rows={previewData.data?.map((row, i) => ({ id: i, ...row })) || []}
                                        columns={previewColumns}
                                        pageSizeOptions={[10, 25, 50]}
                                        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                                        slots={{
                                            toolbar: () => (
                                                <Box sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
                                                    <Box sx={{ flexGrow: 1 }} />
                                                    <Button
                                                        startIcon={isPreviewFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                                        onClick={() => setIsPreviewFullscreen(!isPreviewFullscreen)}
                                                        size="small"
                                                    >
                                                        {isPreviewFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                                                    </Button>
                                                </Box>
                                            ),
                                        }}
                                        sx={{
                                            border: '1px solid #e0e0e0',
                                            height: '100%',
                                            '& .MuiDataGrid-cell': {
                                                borderRight: '1px solid #e0e0e0',
                                            },
                                            '& .MuiDataGrid-columnHeaders': {
                                                borderBottom: '2px solid #e0e0e0',
                                                backgroundColor: '#fafafa',
                                            },
                                            '& .MuiDataGrid-columnHeader': {
                                                borderRight: '1px solid #e0e0e0',
                                            },
                                        }}
                                    />
                                </Box>

                                <Button
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                                    onClick={handleGenerate}
                                    disabled={generating || previewData.valid_rows === 0}
                                >
                                    {generating ? 'Generating...' : '🚀 Generate Merged PDF'}
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}

                {/* TAB 2: LIBRARY */}
                {tabValue === 1 && (
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                            <Typography variant="h6">PDF Library</Typography>
                            <Button startIcon={<RefreshIcon />} onClick={fetchLibrary}>Refresh</Button>
                        </Box>

                        {/* Search Box */}
                        <Box sx={{ mb: 3 }}>
                            <TextField
                                fullWidth
                                placeholder="Search by filename or product name..."
                                value={librarySearchText}
                                onChange={(e) => setLibrarySearchText(e.target.value)}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                }}
                                size="small"
                            />
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <FileUploadArea
                                label="Upload PDF Files"
                                accept=".pdf"
                                onFileSelect={handlePdfUpload}
                                loading={uploadingPdf}
                            />
                        </Box>

                        <Box
                            className={isLibraryFullscreen ? 'datagrid-fullscreen' : ''}
                            sx={{
                                height: isLibraryFullscreen ? '100vh' : 500,
                                width: '100%',
                                transition: 'height 0.3s ease',
                            }}
                        >
                            <DataGrid
                                apiRef={libraryGridRef}
                                rows={libraryFiles
                                    .filter(f => {
                                        if (!librarySearchText) return true;
                                        const searchLower = librarySearchText.toLowerCase();
                                        return (
                                            f.name.toLowerCase().includes(searchLower) ||
                                            (f.product_name && f.product_name.toLowerCase().includes(searchLower))
                                        );
                                    })
                                    .map((f, i) => ({ id: i, ...f }))
                                }
                                columns={libraryColumns}
                                loading={loadingLibrary}
                                pageSizeOptions={[10, 25, 50]}
                                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                                slots={{
                                    toolbar: () => (
                                        <Box sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
                                            <Box sx={{ flexGrow: 1 }} />
                                            <Button
                                                startIcon={isLibraryFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                                onClick={() => setIsLibraryFullscreen(!isLibraryFullscreen)}
                                                size="small"
                                            >
                                                {isLibraryFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                                            </Button>
                                        </Box>
                                    ),
                                }}
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    height: '100%',
                                    '& .MuiDataGrid-cell': {
                                        borderRight: '1px solid #e0e0e0',
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        borderBottom: '2px solid #e0e0e0',
                                        backgroundColor: '#fafafa',
                                    },
                                    '& .MuiDataGrid-columnHeader': {
                                        borderRight: '1px solid #e0e0e0',
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                )}

                {/* TAB 3: EXCLUSIONS */}
                {tabValue === 2 && (
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                            <Typography variant="h6">Excluded Products (No Label Needed)</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<UploadIcon />}
                                    onClick={() => setAddExclusionOpen(true)}
                                >
                                    Add Product
                                </Button>
                                <Button startIcon={<RefreshIcon />} onClick={fetchExclusions}>Refresh</Button>
                            </Box>
                        </Box>

                        {/* Search Box */}
                        <Box sx={{ mb: 3 }}>
                            <TextField
                                fullWidth
                                placeholder="Search excluded products..."
                                value={exclusionSearchText}
                                onChange={(e) => setExclusionSearchText(e.target.value)}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                }}
                                size="small"
                            />
                        </Box>

                        {/* DataGrid */}
                        <Box sx={{ height: 500, width: '100%' }}>
                            <DataGrid
                                rows={exclusions
                                    .filter(e => {
                                        if (!exclusionSearchText) return true;
                                        const searchLower = exclusionSearchText.toLowerCase();
                                        return (
                                            e.product_name?.toLowerCase().includes(searchLower) ||
                                            e.sku?.toLowerCase().includes(searchLower) ||
                                            e.product_id?.toString().includes(searchLower)
                                        );
                                    })
                                    .map((e, i) => ({ id: e.id || i, ...e }))
                                }
                                columns={exclusionColumns}
                                loading={loadingExclusions}
                                pageSizeOptions={[10, 25, 50]}
                                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                            />
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <strong>{deleteConfirm}</strong>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button onClick={handleDeletePdf} color="error" autoFocus>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Add Exclusion Dialog */}
            <Dialog open={addExclusionOpen} onClose={() => setAddExclusionOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Product to Exclusion List</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Enter the Product ID and Variation ID (if applicable) to exclude from label generation.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Product ID"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={newProductId}
                        onChange={(e) => setNewProductId(e.target.value)}
                        required
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Variation ID (optional)"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={newVariationId}
                        onChange={(e) => setNewVariationId(e.target.value)}
                        helperText="Leave empty for simple products, enter variation ID for variable products"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setAddExclusionOpen(false);
                        setNewProductId('');
                        setNewVariationId('');
                    }}>
                        Cancel
                    </Button>
                    <Button onClick={handleAddExclusionManually} variant="contained" color="primary">
                        Add to Exclusion List
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
